import { useEffect, useMemo, useRef, useState } from 'react';
import { Robot, ShieldWarning, Sparkle } from '@phosphor-icons/react';
import { useCompany, useWorkspace } from '../App';
import { api, ApiError } from '../lib/api';
import { faNumber, jalaliDate, metricDisplay, severityLabel } from '../lib/format';
import { Card, EmptyState, InlineNotice, PageHeader, StatusBadge } from '../components/Ui';
import { ScoreRing, Sparkline } from '../components/Charts';
import { ChatComposer, ComposerRetry, type UploadPolicy } from '../components/ChatComposer';
import type { AiConversation, AiMessage, AnalysisModeKey, Dashboard, QualityIssue } from '../types';

/** §۶ تحلیل زنده مالی — the four things it names, kept apart on screen. */
const LIVE_GROUPS: Array<{ kind: string; titleFa: string; emptyFa: string }> = [
  { kind: 'alert', titleFa: 'هشدارها', emptyFa: 'هشداری برای این دوره ثبت نشده است.' },
  { kind: 'opportunity', titleFa: 'فرصت‌ها', emptyFa: 'فرصتی برای این دوره شناسایی نشده است.' },
  { kind: 'suggestion', titleFa: 'پیشنهادها', emptyFa: 'پیشنهادی برای این دوره ثبت نشده است.' },
];

/** The ratios worth a shape rather than a single figure. */
const LIVE_METRICS = ['current_ratio', 'debt_to_assets', 'gross_margin', 'net_margin', 'return_on_assets', 'return_on_equity'];

export function AiCenterPage() {
  const { fiscalYear, periodKey, currency, bootstrap, can, navigate } = useWorkspace();
  const company = useCompany();
  const modes = bootstrap.capabilities.analysisModes;
  const [mode, setMode] = useState<AnalysisModeKey>(bootstrap.settings.defaultAiMode);
  const [conversations, setConversations] = useState<AiConversation[]>([]);
  const [conversationId, setConversationId] = useState('');
  const [messages, setMessages] = useState<AiMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [uploadNotice, setUploadNotice] = useState('');
  const [error, setError] = useState('');
  const askAbort = useRef<AbortController | null>(null);
  const [integrity, setIntegrity] = useState<{ noteFa: string; findings: QualityIssue[]; reliable: boolean } | null>(null);
  const [integrityBusy, setIntegrityBusy] = useState(false);
  const [live, setLive] = useState<Dashboard | null>(null);
  const streamRef = useRef<HTMLDivElement>(null);

  // §۶ تحلیل زنده مالی reads the same engine output the command centre does.
  // Recomputing it here would be a second opinion on the same period, and two
  // pages disagreeing about سلامت مالی is worse than one page not showing it.
  useEffect(() => {
    if (!fiscalYear) return undefined;
    let cancelled = false;
    void api.dashboard(company.id, fiscalYear.id, periodKey)
      .then((result) => { if (!cancelled) setLive(result); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [company.id, fiscalYear, periodKey]);

  useEffect(() => {
    setConversationId('');
    setMessages([]);
    if (!fiscalYear) { setConversations([]); return; }
    let cancelled = false;
    void api.conversations(company.id, { fiscalYearId: fiscalYear.id, periodKey, mode })
      .then((result) => { if (!cancelled) setConversations(result.conversations); })
      .catch(() => { if (!cancelled) setConversations([]); });
    return () => { cancelled = true; };
  }, [company.id, fiscalYear, periodKey, mode]);

  useEffect(() => {
    if (!conversationId) { setMessages([]); return; }
    void api.messages(company.id, conversationId).then((result) => setMessages(result.messages)).catch(() => undefined);
  }, [company.id, conversationId]);

  useEffect(() => {
    streamRef.current?.scrollTo({ top: streamRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  /**
   * Files attached to a question are a real import, not a chat decoration: they
   * go through the same run the processing page starts. They are deliberately
   * NOT folded into the answer that follows — the ledger has to be parsed,
   * mapped and approved first — so the notice says so rather than letting the
   * reader assume the reply already accounts for what they just sent.
   */
  async function sendFiles(files: File[]) {
    if (!fiscalYear || !files.length) return;
    const { run } = await api.startRun(company.id, files, fiscalYear.id, periodKey, 'auto');
    setUploadNotice(
      `${faNumber(files.length)} فایل برای پردازش فرستاده شد. تا پایان پردازش و تأیید نگاشت‌ها،`
      + ' ارقام این فایل‌ها وارد پاسخ‌ها نمی‌شود.',
    );
    return run;
  }

  async function handleSend(text: string, files: File[]) {
    if (!fiscalYear) return;
    setError('');
    setUploadNotice('');

    // Uploads first and un-caught: a failure here must reach the composer, which
    // keeps the text and the files rather than silently dropping them.
    let uploaded = false;
    if (files.length) {
      await sendFiles(files);
      uploaded = true;
    }
    if (!text) return;

    const controller = new AbortController();
    askAbort.current = controller;
    setBusy(true);
    // The question appears immediately with a local id; the server's copy
    // replaces it when the reply lands.
    setMessages((current) => [...current, {
      id: `local-${current.length}`, role: 'user', content: text, citations: [], model: '', createdAt: new Date().toISOString(),
    }]);
    try {
      const result = await api.ask(company.id, {
        question: text, mode, fiscalYearId: fiscalYear.id, periodKey, conversationId: conversationId || undefined,
      }, controller.signal);
      setConversationId(result.conversationId);
      const refreshed = await api.messages(company.id, result.conversationId);
      setMessages(refreshed.messages);
      setConversations((await api.conversations(company.id, {
        fiscalYearId: fiscalYear.id, periodKey, mode,
      })).conversations);
    } catch (caught) {
      setMessages((current) => current.filter((message) => !message.id.startsWith('local-')));
      // An abort is the user's own doing, so it is not reported as a failure —
      // but the question goes back to the composer either way.
      if (!(caught instanceof DOMException && caught.name === 'AbortError')) {
        const message = caught instanceof ApiError ? caught.message : 'پاسخ دریافت نشد.';
        // The files, if any, are already imported and must not be sent twice.
        throw new ComposerRetry(message, { text: true, files: !uploaded });
      }
    } finally {
      askAbort.current = null;
      setBusy(false);
      setStopping(false);
    }
  }

  function stopAnswer() {
    if (!askAbort.current) return;
    setStopping(true);
    askAbort.current.abort();
  }

  /**
   * The ceilings and the format list come from the server's own capabilities
   * rather than constants kept in step by hand, so raising
   * `FINOJAN_MAX_UPLOAD_FILES` cannot leave the composer refusing files the
   * server would have taken.
   */
  const uploadPolicy = useMemo<UploadPolicy | undefined>(() => {
    // Hidden, not disabled, when this role cannot import or there is nowhere to
    // import into: a control that can never act is worse than no control.
    if (!can('import.upload') || !fiscalYear) return undefined;
    return {
      acceptedExtensions: new Set(bootstrap.capabilities.uploadFormats.map((item) => item.toLocaleLowerCase('en-US'))),
      maxFiles: bootstrap.capabilities.maxUploadFiles,
      // The server measures the request body, so this is the budget for the
      // whole batch rather than a per-file allowance.
      maxTotalBytes: bootstrap.capabilities.maxUploadBytes,
    };
  }, [bootstrap.capabilities, can, fiscalYear]);

  const chatDisabledReason = !can('ai.chat')
    ? 'نقش شما اجازهٔ گفتگو با تحلیل‌گر را ندارد.'
    : !bootstrap.capabilities.ai
      ? 'تحلیل هوشمند پیکربندی نشده است.'
      : '';

  async function runIntegrity() {
    if (!fiscalYear) return;
    setIntegrityBusy(true);
    setError('');
    try {
      setIntegrity(await api.integrity(company.id, fiscalYear.id));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'بررسی صحت اطلاعات انجام نشد.');
    } finally {
      setIntegrityBusy(false);
    }
  }

  if (!fiscalYear) {
    return (
      <EmptyState
        title="سال مالی انتخاب نشده است"
        description="مرکز هوش مالی روی ارقام یک دوره مشخص کار می‌کند. ابتدا سال مالی را انتخاب کنید."
        action={<button className="button button--primary" onClick={() => navigate('companies')}>رفتن به شرکت‌ها</button>}
      />
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="مرکز هوش مالی"
        title="گفتگو با تحلیل‌گر"
        description={`${company.name} — ${fiscalYear.labelFa}`}
        actions={(
          <button className="button button--soft" onClick={runIntegrity} disabled={integrityBusy}>
            <ShieldWarning size={16} /> {integrityBusy ? 'در حال بررسی…' : 'بررسی صحت اطلاعات'}
          </button>
        )}
      />

      {!bootstrap.capabilities.ai && (
        <InlineNotice tone="warning">
          تحلیل هوشمند پیکربندی نشده است، بنابراین گفتگو در دسترس نیست. «بررسی صحت اطلاعات» بدون آن هم
          کار می‌کند و یافته‌های موتور کنترل کیفیت را نشان می‌دهد.
        </InlineNotice>
      )}

      {bootstrap.capabilities.ai && (
        <p className="hint">
          امروز {faNumber(bootstrap.capabilities.aiCallsToday)} از {faNumber(bootstrap.capabilities.aiDailyBudget)} فراخوانی مجاز مصرف شده است.
        </p>
      )}

      {error && <InlineNotice tone="danger">{error}</InlineNotice>}

      {/* §۶ تحلیل زنده مالی — سلامت مالی، KPI، هشدارها، فرصت‌ها، پیشنهادها.
          Produced by the rule engine without a model, so it is here whether or
          not the assistant is configured. */}
      {live?.ready && (
        <div className="live-grid">
          {live.health && (
            <Card>
              <div className="card-heading"><h2>سلامت مالی</h2></div>
              <ScoreRing
                score={live.health.score}
                coverage={live.health.coverage}
                labelFa="امتیاز سلامت مالی"
                captionFa={live.health.score === null
                  ? 'شاخص‌های مبنا برای این دوره قابل محاسبه نبود.'
                  : `بر پایه ${faNumber(Math.round(live.health.coverage * 100))}٪ از شاخص‌های مبنا.`}
              />
            </Card>
          )}

          <Card className="live-metrics">
            <div className="card-heading"><h2>تحلیل سریع شاخص‌ها</h2></div>
            <div className="health-list">
              {LIVE_METRICS
                .map((key) => (live.metrics ?? []).find((metric) => metric.key === key))
                .filter((metric): metric is NonNullable<typeof metric> => Boolean(metric))
                .map((metric) => {
                  // The shape is drawn from the period series, not invented: a
                  // ratio with only one computed period gets no sparkline.
                  const series = live.trend?.series.find((item) => item.key === metric.key);
                  const values = (live.trend?.periods ?? [])
                    .filter((period) => period.key.startsWith('m'))
                    .map((period) => series?.points[period.key] ?? null);
                  return (
                    <div key={metric.key} className="health-list__row">
                      <span>{metric.labelFa}</span>
                      <Sparkline values={values} labelFa={metric.labelFa} />
                      <b className={metric.value === null ? 'is-missing' : ''}>
                        {/* The reader's own unit, not a fixed one — every
                            other page on the scope bar honours this toggle. */}
                        {metricDisplay(metric, company.reportingCurrency, currency)}
                      </b>
                    </div>
                  );
                })}
            </div>
          </Card>

          <Card className="live-insights">
            <div className="card-heading"><h2>بینش‌ها و پیشنهادهای هوش مالی</h2></div>
            {LIVE_GROUPS.map((group) => {
              const items = (live.insights ?? []).filter((insight) => insight.kind === group.kind);
              return (
                <div key={group.kind} className="insight-group">
                  <h3>{group.titleFa}</h3>
                  {items.length === 0 ? <p className="hint">{group.emptyFa}</p> : (
                    <ul className="alert-list">
                      {items.map((insight, index) => (
                        <li key={`${insight.titleFa}-${index}`}>
                          <span className={`severity severity--${insight.severity}`}>{severityLabel(insight.severity)}</span>
                          <div>
                            <strong>{insight.titleFa}</strong>
                            <p>{insight.bodyFa}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </Card>
        </div>
      )}

      {integrity && (
        <Card>
          <div className="card-heading">
            <h2>بررسی صحت اطلاعات</h2>
            <StatusBadge status={integrity.reliable ? 'succeeded' : 'failed'} label={integrity.reliable ? 'قابل اتکا' : 'نیازمند رفع مغایرت'} />
          </div>
          <p>{integrity.noteFa}</p>
          {integrity.findings.length > 0 && (
            <ul className="alert-list">
              {integrity.findings.map((finding) => (
                <li key={finding.titleFa}>
                  <span className={`severity severity--${finding.severity}`}>{severityLabel(finding.severity)}</span>
                  <div>
                    <strong>{finding.titleFa}</strong>
                    <p>{finding.descriptionFa}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      <div className="ai-layout">
        <aside className="ai-sidebar">
          <Card>
            <div className="card-heading"><h2>حالت تحلیل</h2></div>
            <div className="ai-modes">
              {modes.map((item) => (
                <button
                  key={item.key}
                  className={mode === item.key ? 'active' : ''}
                  onClick={() => setMode(item.key as AnalysisModeKey)}
                  type="button"
                >
                  <strong>{item.labelFa}</strong>
                  <small>{item.descriptionFa}</small>
                </button>
              ))}
            </div>
          </Card>

          <Card>
            <div className="card-heading"><h2>گفتگوهای پیشین</h2></div>
            {conversations.length === 0 ? (
              <p className="hint">هنوز گفتگویی ثبت نشده است.</p>
            ) : (
              <div className="report-history">
                <button
                  className={conversationId === '' ? 'active' : ''}
                  onClick={() => setConversationId('')}
                  type="button"
                >
                  <strong>گفتگوی جدید</strong>
                </button>
                {conversations.map((conversation) => (
                  <button
                    key={conversation.id}
                    className={conversationId === conversation.id ? 'active' : ''}
                    onClick={() => setConversationId(conversation.id)}
                    type="button"
                  >
                    <strong>{conversation.titleFa || 'بدون عنوان'}</strong>
                    <small>{jalaliDate(conversation.updatedAt)}</small>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </aside>

        <Card className="chat-card">
          <div className="chat-stream" ref={streamRef}>
            {messages.length === 0 && !busy && (
              <div className="chat-welcome">
                <Sparkle size={30} weight="fill" />
                <h3>چه چیزی را می‌خواهید بدانید؟</h3>
                <p>
                  پاسخ‌ها فقط از ارقام همین دوره ساخته می‌شوند. اگر عددی در داده نباشد، تحلیل‌گر آن را
                  نمی‌سازد و می‌گوید چه چیزی کم است.
                </p>
              </div>
            )}
            {messages.map((message) => (
              <div key={message.id} className={message.role === 'user' ? 'chat-message chat-message--user' : 'chat-message'}>
                {message.role === 'assistant' && <span className="assistant-avatar"><Robot size={17} weight="fill" /></span>}
                <div>
                  {/* No model chip. Which model answered says nothing about
                      whether the figure is right, and showing it invites the
                      reader to trust the answer by its brand — the same reason
                      the model chain is not a setting. */}
                  <p>{message.content}</p>
                </div>
              </div>
            ))}
            {busy && <div className="chat-message"><span className="assistant-avatar"><Robot size={17} weight="fill" /></span><div className="typing"><i /><i /><i /></div></div>}
          </div>

          {uploadNotice && (
            <div className="chat-notice">
              <InlineNotice tone="info">
                {uploadNotice}{' '}
                <button type="button" className="link-button" onClick={() => navigate('processing')}>
                  رفتن به پردازش اطلاعات
                </button>
              </InlineNotice>
            </div>
          )}

          <ChatComposer
            isResponding={busy}
            isStopping={stopping}
            disabled={chatDisabledReason !== ''}
            disabledReason={chatDisabledReason || undefined}
            onSend={handleSend}
            onStop={stopAnswer}
            uploadPolicy={uploadPolicy}
          />
        </Card>
      </div>
    </>
  );
}
